import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Input, Textarea, Select, Card } from "../components/ui";

function NuovaAzioneCorrettivaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const interventoId = searchParams.get("interventoId") || "";
  const rsaId = searchParams.get("rsaId") || "";
  const fornitoreId = searchParams.get("fornitoreId") || "";

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [utenti, setUtenti] = useState([]);
  const [interventoNome, setInterventoNome] = useState("");
  const [assegnamentoMode, setAssegnamentoMode] = useState("utente"); // "utente" | "libero"

  const [formData, setFormData] = useState({
    descrizione: "",
    interventoId,
    rsaId,
    fornitoreId,
    soggettoAssegnato: "",
    emailAssegnato: "",
    scadenza: "",
    priorita: 100000001, // Media
    note: "",
  });

  useEffect(() => {
    // Carica utenti per dropdown
    apiRequest("/azioni-correttive/utenti")
      .then((res) => setUtenti(res.value || []))
      .catch(() => {});

    // Carica nome intervento
    if (interventoId) {
      apiRequest(`/interventi/${interventoId}`)
        .then((data) => setInterventoNome(data.nome || ""))
        .catch(() => {});
    }
  }, [apiRequest, interventoId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    const selectedUser = utenti.find((u) => u.id === userId);
    if (selectedUser) {
      setFormData((prev) => ({
        ...prev,
        soggettoAssegnato: selectedUser.displayName,
        emailAssegnato: selectedUser.email,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.descrizione?.trim())
      newErrors.descrizione = "La descrizione e obbligatoria";
    if (!formData.soggettoAssegnato?.trim())
      newErrors.soggettoAssegnato = "Il soggetto assegnato e obbligatorio";
    if (!formData.scadenza) newErrors.scadenza = "La scadenza e obbligatoria";
    if (!formData.interventoId)
      newErrors.interventoId = "L'intervento e obbligatorio";
    if (!formData.rsaId) newErrors.rsaId = "La RSA e obbligatoria";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      showToast("Correggi gli errori nel form", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        priorita: parseInt(formData.priorita),
      };
      // Remove empty values
      if (!payload.fornitoreId) delete payload.fornitoreId;
      if (!payload.note) delete payload.note;

      const result = await apiRequest("/azioni-correttive", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      showToast("Azione correttiva creata con successo", "success");
      navigate(`/azioni-correttive/${result.id}`);
    } catch (error) {
      console.error("Error creating azione correttiva:", error);
      showToast(error.message || "Errore nella creazione", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-2 text-sm text-gray-500">
        <Link to="/azioni-correttive" className="hover:text-primary-600">
          Azioni Correttive
        </Link>
        <span className="mx-2">/</span>
        <span>Nuova</span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Nuova Azione Correttiva
      </h1>

      {interventoNome && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-700">
            Intervento collegato:{" "}
            <span className="font-medium">{interventoNome}</span>
          </p>
        </div>
      )}

      <Card>
        <div className="space-y-5">
          <Textarea
            label="Descrizione dell'azione richiesta *"
            name="descrizione"
            value={formData.descrizione}
            onChange={handleChange}
            rows={4}
            placeholder="Descrivere l'azione correttiva necessaria..."
            error={errors.descrizione}
          />

          <Select
            label="Priorita *"
            name="priorita"
            value={formData.priorita}
            onChange={handleChange}
            options={[
              { value: 100000000, label: "Bassa" },
              { value: 100000001, label: "Media" },
              { value: 100000002, label: "Alta" },
              { value: 100000003, label: "Critica" },
            ]}
          />

          <Input
            label="Scadenza revisione *"
            name="scadenza"
            type="date"
            value={formData.scadenza}
            onChange={handleChange}
            error={errors.scadenza}
          />

          {/* Assegnazione: toggle tra dropdown utenti e campo libero */}
          <div>
            <div className="flex items-center gap-4 mb-3">
              <label className="text-sm font-medium text-gray-700">
                Soggetto incaricato *
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAssegnamentoMode("utente")}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    assegnamentoMode === "utente"
                      ? "bg-primary-100 border-primary-300 text-primary-700"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Utente portale
                </button>
                <button
                  type="button"
                  onClick={() => setAssegnamentoMode("libero")}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    assegnamentoMode === "libero"
                      ? "bg-primary-100 border-primary-300 text-primary-700"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Inserimento libero
                </button>
              </div>
            </div>

            {assegnamentoMode === "utente" ? (
              <select
                onChange={handleUserSelect}
                className="input w-full"
                defaultValue=""
              >
                <option value="" disabled>
                  Seleziona un utente...
                </option>
                {utenti.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName} ({u.email})
                  </option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Nome soggetto"
                  name="soggettoAssegnato"
                  value={formData.soggettoAssegnato}
                  onChange={handleChange}
                  placeholder="Nome e cognome"
                  error={errors.soggettoAssegnato}
                />
                <Input
                  label="Email (per reminder)"
                  name="emailAssegnato"
                  type="email"
                  value={formData.emailAssegnato}
                  onChange={handleChange}
                  placeholder="email@esempio.com"
                />
              </div>
            )}

            {formData.soggettoAssegnato && assegnamentoMode === "utente" && (
              <p className="text-sm text-gray-600 mt-2">
                Assegnato a:{" "}
                <span className="font-medium">
                  {formData.soggettoAssegnato}
                </span>
                {formData.emailAssegnato && (
                  <span className="text-gray-400">
                    {" "}
                    ({formData.emailAssegnato})
                  </span>
                )}
              </p>
            )}
          </div>

          <Textarea
            label="Note aggiuntive"
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={3}
            placeholder="Note opzionali..."
          />
        </div>
      </Card>

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="ghost" onClick={() => navigate(-1)} disabled={saving}>
          Annulla
        </Button>
        <Button onClick={handleSubmit} loading={saving}>
          Crea Azione Correttiva
        </Button>
      </div>
    </div>
  );
}

export default NuovaAzioneCorrettivaPage;
